//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import LoadingBar from '@components/LoadingBar';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { PrimaryButton as CopyButton } from '@components/atoms/button/Button';
import { Icon } from '@components/atoms/icon/Icon';
import OnePageTemplate, {
  IOnePageProps,
  OnePageLoadingState,
} from '@components/one-page/OnePageTemplate';
import { TRACKING_ACTION, TRACKING_CATEGORY } from '@tracker/trackerModel';
import { getDomainFromUrl } from '@utils/urlUtils';

type Props = IOnePageProps;

const OnePageLinkAndButton = (props: Props) => {
  const { cuid, className } = props;

  const renderButton = (
    copyLink: (event: React.MouseEvent) => Promise<void>,
    loadingState: OnePageLoadingState,
    url: string,
  ) => (
    <Container>
      <LabelsContainer>
        <InputLabel>ONE Page URL</InputLabel>
        {url && (
          <PreviewLink
            href={url}
            target='_blank'
            trackingId={`${TRACKING_CATEGORY.ONE_PAGE}-${TRACKING_ACTION.ONE_PAGE_PREVIEW_LINK}`}
          >
            <PreviewIcon />
            Preview on {getDomainFromUrl(url, false)}
          </PreviewLink>
        )}
      </LabelsContainer>
      <LinkContainer>
        <InputContainer>
          <UrlInput value={url} disabled={true} type='text' />
          <InputLoader
            shouldShowLoader={loadingState === OnePageLoadingState.Loading}
          />
        </InputContainer>
        <CopyButton iconName={'link'} onClick={copyLink}>
          Copy
        </CopyButton>
      </LinkContainer>
    </Container>
  );

  const renderTooltip = () => (
    <CopiedTooltip>
      <UrlCopiedIcon />
      URL Copied to Clipboard
    </CopiedTooltip>
  );

  return (
    <OnePageTemplate
      className={className}
      cuid={cuid}
      preload={true}
      renderCopyElement={renderButton}
      renderTooltip={renderTooltip}
    />
  );
};

const PreviewLink = styled(PrimaryAnchor)`
  font-size: 12px;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
`;

const PreviewIcon = styled(Icon).attrs({
  name: 'linkout',
})(
  ({ theme }) => `
  vertical-align: middle;
  margin-right: ${theme.spacings[1]};
  height: 12px;
  width: 12px;
`,
);

const LabelsContainer = styled.div(
  ({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: ${theme.spacings[1]};
`,
);

const InputLabel = styled.div.attrs({
  className: 'cu-text-subsection-2',
})`
  /** Ensures this div and the Preview link floating to its right
    * have the same height to avoid pop-in effects that change
    * the height of the component when the URL loads
    */
  line-height: 1.5;
`;

const UrlCopiedIcon = styled(Icon).attrs({
  name: 'circle-checkmark-filled',
})(
  ({ theme }) => `
  && {
    margin-right: ${theme.spacings[2]};
    color: ${theme.colors.green6};
  }
`,
);

const CopiedTooltip = styled.div.attrs({
  className: 'cu-text-body',
})(
  ({ theme }) => `
  padding: ${theme.spacings[2]} ${theme.spacings[4]};
`,
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const LinkContainer = styled.div`
  display: flex;
`;

const UrlInput = styled.input.attrs({
  className: 'cu-form-control',
})`
  width: 100%;
`;

const InputLoader = styled(LoadingBar)`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 4px;
`;

const InputContainer = styled.div(
  ({ theme }) => `
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  flex: 1;
  margin-right: ${theme.spacings[2]};
`,
);

export default OnePageLinkAndButton;
